// Admin settings page for named Consultation Summary templates.
// Sidebar lists all templates; edit pane on the right shows the selected
// template's name + next steps + timeline milestones.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Plus, Trash2, Loader2, RotateCcw, ClipboardCheck, Copy,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyTemplate = () => ({
  id: null,
  name: 'New template',
  next_steps: [],
  timeline_items: [],
});

export default function ConsultationTemplateSettings() {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const auth = useMemo(() => ({ Authorization: `Basic ${btoa(':' + password)}` }), [password]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null); // Working copy of the selected template

  const loadAll = async () => {
    const { data } = await axios.get(`${API}/admin/settings/consultation-templates`, { headers: auth });
    const list = data.templates || [];
    setTemplates(list);
    if (list.length > 0) {
      setSelectedId((prev) => list.find((t) => t.id === prev) ? prev : list[0].id);
    } else {
      setSelectedId(null);
    }
  };

  useEffect(() => {
    if (!password) { navigate('/admin/login'); return; }
    (async () => {
      try { await loadAll(); }
      catch { toast.error('Failed to load templates'); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whenever the selection changes, load a working copy into the editor.
  useEffect(() => {
    if (!selectedId) { setDraft(null); return; }
    const t = templates.find((x) => x.id === selectedId);
    if (t) setDraft(JSON.parse(JSON.stringify(t)));
  }, [selectedId, templates]);

  const isDirty = useMemo(() => {
    if (!draft) return false;
    const original = templates.find((x) => x.id === draft.id);
    if (!original) return true; // new template
    return JSON.stringify(original) !== JSON.stringify(draft);
  }, [draft, templates]);

  const save = async () => {
    if (!draft) return;
    if (!draft.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        next_steps: draft.next_steps,
        timeline_items: draft.timeline_items,
      };
      if (draft.id) {
        await axios.put(`${API}/admin/settings/consultation-templates/${draft.id}`, payload, { headers: auth });
      } else {
        const { data } = await axios.post(`${API}/admin/settings/consultation-templates`, payload, { headers: auth });
        setSelectedId(data.template.id);
      }
      await loadAll();
      toast.success(`Saved "${payload.name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addTemplate = () => {
    const draftTpl = emptyTemplate();
    // Add to the list *without* an id — save will POST it and swap the id in
    setTemplates((prev) => [...prev, draftTpl]);
    setSelectedId(null);
    setDraft(draftTpl);
  };

  const duplicateSelected = async () => {
    if (!draft) return;
    if (!draft.id) { toast.error('Save this template first before duplicating'); return; }
    setSaving(true);
    try {
      const payload = {
        name: `${draft.name} (copy)`,
        next_steps: draft.next_steps,
        timeline_items: draft.timeline_items,
      };
      const { data } = await axios.post(`${API}/admin/settings/consultation-templates`, payload, { headers: auth });
      await loadAll();
      setSelectedId(data.template.id);
      toast.success(`Duplicated as "${payload.name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Duplicate failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteSelected = async () => {
    if (!draft?.id) {
      // Unsaved new one — just drop from local list
      setTemplates((prev) => prev.filter((t) => t !== draft));
      const remaining = templates.filter((t) => t.id);
      setSelectedId(remaining[0]?.id || null);
      return;
    }
    if (!window.confirm(`Delete template "${draft.name}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/settings/consultation-templates/${draft.id}`, { headers: auth });
      toast.success('Template deleted');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Reset ALL templates back to the built-in defaults (UK / US / EU)? Any customisations will be lost.')) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/settings/consultation-templates/reset`, {}, { headers: auth });
      await loadAll();
      toast.success('Reset to defaults');
    } catch (e) {
      toast.error('Reset failed');
    } finally {
      setSaving(false);
    }
  };

  // Draft helpers
  const updateStep = (i, k, v) => setDraft((d) => ({ ...d, next_steps: d.next_steps.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const addStep = () => setDraft((d) => ({ ...d, next_steps: [...d.next_steps, { action: '', owner: 'Client', due_date: 'TBD' }] }));
  const removeStep = (i) => setDraft((d) => ({ ...d, next_steps: d.next_steps.filter((_, idx) => idx !== i) }));

  const updateTl = (i, k, v) => setDraft((d) => ({ ...d, timeline_items: d.timeline_items.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
  const addTl = () => setDraft((d) => ({ ...d, timeline_items: [...d.timeline_items, { when: '', milestone: '', when_date: '' }] }));
  const removeTl = (i) => setDraft((d) => ({ ...d, timeline_items: d.timeline_items.filter((_, idx) => idx !== i) }));

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center text-foreground/60">
      <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="consultation-template-settings">
      <div className="bg-white border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Link to="/admin/consultation-summaries" className="p-2 rounded-lg hover:bg-gray-100"><ArrowLeft className="w-5 h-5" /></Link>
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center"><ClipboardCheck className="w-5 h-5 text-primary" /></div>
            <div>
              <h1 className="text-base sm:text-xl font-bold text-primary">Consultation Templates</h1>
              <p className="text-xs text-foreground/60">Named starter templates (UK, US, EU…) for the "Prefill" button</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={resetToDefaults} disabled={saving} data-testid="reset-btn">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Reset all
            </Button>
            <Button onClick={save} disabled={saving || !draft || !isDirty} data-testid="save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar list */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-border rounded-xl p-3 sticky top-24">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs font-bold uppercase tracking-wide text-primary">Templates</h2>
              <Button size="sm" variant="outline" onClick={addTemplate} data-testid="add-template-btn">
                <Plus className="w-4 h-4 mr-1" /> New
              </Button>
            </div>
            <ul className="space-y-1">
              {templates.map((t) => (
                <li key={t.id || t.name}>
                  <button
                    onClick={() => setSelectedId(t.id)}
                    className={`w-full text-left px-2.5 py-2 rounded-md text-sm border transition-colors ${
                      (t.id ? selectedId === t.id : draft === t)
                        ? 'border-primary bg-primary/5 text-primary font-semibold'
                        : 'border-transparent hover:bg-gray-50'
                    }`}
                    data-testid={`template-tab-${t.id || 'new'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate">{t.name}</span>
                      {!t.id && <span className="text-[10px] text-orange-600 ml-2">unsaved</span>}
                    </div>
                    <div className="text-[11px] text-foreground/50 mt-0.5">
                      {(t.next_steps?.length || 0)} steps · {(t.timeline_items?.length || 0)} milestones
                    </div>
                  </button>
                </li>
              ))}
              {templates.length === 0 && (
                <p className="text-xs text-foreground/50 py-4 text-center">No templates yet. Click "Reset all" to bring back the built-ins.</p>
              )}
            </ul>
          </div>
        </div>

        {/* Editor pane */}
        <div className="lg:col-span-3 space-y-5">
          {!draft ? (
            <div className="bg-white border border-border rounded-xl p-10 text-center text-foreground/50 text-sm">
              Pick a template on the left to edit it — or click <b>New</b> to create one.
            </div>
          ) : (
            <>
              <div className="bg-white border border-border rounded-xl p-5">
                <div className="flex items-center justify-between mb-3 gap-2">
                  <div className="flex-1">
                    <Label className="text-[11px]">Template name</Label>
                    <Input
                      value={draft.name}
                      onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                      placeholder="e.g. Cayman → UK"
                      data-testid="template-name"
                    />
                  </div>
                  <Button variant="outline" onClick={duplicateSelected} disabled={saving || !draft.id} data-testid="duplicate-template-btn" title={draft.id ? 'Save a copy of this template' : 'Save this template first'}>
                    <Copy className="w-4 h-4 mr-1" /> Duplicate
                  </Button>
                  <Button variant="outline" onClick={deleteSelected} className="text-red-600 hover:text-red-700" data-testid="delete-template-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </Button>
                </div>
              </div>

              {/* Next steps */}
              <div className="bg-white border border-border rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-bold uppercase tracking-wide text-primary">Standard next-step actions</h2>
                  <Button variant="outline" size="sm" onClick={addStep} data-testid="add-step-btn"><Plus className="w-4 h-4 mr-1" /> Add step</Button>
                </div>
                <p className="text-[11px] text-foreground/60 mb-3">
                  Each row seeds one bullet on the "Agreed next steps" list. Owner appears on the chip on the PDF.
                </p>
                <div className="space-y-2">
                  {draft.next_steps.map((s, i) => (
                    <div key={i} className="border border-border rounded-lg p-3 space-y-2 bg-gray-50/40" data-testid={`step-row-${i}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-foreground/60 uppercase">Step {i + 1}</span>
                        <button onClick={() => removeStep(i)} className="p-1 rounded hover:bg-red-50 text-red-600" data-testid={`step-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                      <textarea
                        value={s.action}
                        onChange={(e) => updateStep(i, 'action', e.target.value)}
                        rows={2}
                        placeholder="Action…"
                        data-testid={`step-action-${i}`}
                        className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[11px]">Owner</Label>
                          <select
                            value={s.owner || 'Client'}
                            onChange={(e) => updateStep(i, 'owner', e.target.value)}
                            data-testid={`step-owner-${i}`}
                            className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                          >
                            <option value="Client">Client</option>
                            <option value="Pawsome">Pawsome</option>
                            <option value="">— None —</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-[11px]">Default due date</Label>
                          <select
                            value={s.due_date === 'TBD' || !s.due_date ? 'TBD' : 'blank'}
                            onChange={(e) => updateStep(i, 'due_date', e.target.value === 'TBD' ? 'TBD' : '')}
                            data-testid={`step-due-${i}`}
                            className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                          >
                            <option value="TBD">TBD (To be determined)</option>
                            <option value="blank">Leave blank</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                  {draft.next_steps.length === 0 && (
                    <p className="text-xs text-foreground/50 py-6 text-center">No steps in this template — click "Add step".</p>
                  )}
                </div>
              </div>

              {/* Timeline */}
              <div className="bg-white border border-border rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-bold uppercase tracking-wide text-primary">Standard timeline milestones</h2>
                  <Button variant="outline" size="sm" onClick={addTl} data-testid="add-tl-btn"><Plus className="w-4 h-4 mr-1" /> Add milestone</Button>
                </div>
                <p className="text-[11px] text-foreground/60 mb-3">
                  "When" is the T-week label shown on the pill. "Milestone" is the description beside it. Exact dates are added later per consultation.
                </p>
                <div className="space-y-2">
                  {draft.timeline_items.map((t, i) => (
                    <div key={i} className="grid grid-cols-[110px_1fr_auto] gap-2 items-center" data-testid={`tl-row-${i}`}>
                      <Input
                        value={t.when || ''}
                        onChange={(e) => updateTl(i, 'when', e.target.value)}
                        placeholder="T-6 weeks"
                        data-testid={`tl-when-${i}`}
                      />
                      <Input
                        value={t.milestone || ''}
                        onChange={(e) => updateTl(i, 'milestone', e.target.value)}
                        placeholder="Milestone description"
                        data-testid={`tl-milestone-${i}`}
                      />
                      <button onClick={() => removeTl(i)} className="p-2 rounded hover:bg-red-50 text-red-600" data-testid={`tl-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {draft.timeline_items.length === 0 && (
                    <p className="text-xs text-foreground/50 py-6 text-center">No milestones — click "Add milestone".</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
